/**
 * ScanContext — barcode scanner input for the admin panel.
 *
 * Scanners in keyboard-emulation mode "type" the code and press Enter. This
 * provider tells a scan apart from a person typing by speed: a burst of at
 * least MIN_LENGTH characters, each within MAX_GAP_MS of the previous one,
 * ending in Enter. Nobody types that fast by hand.
 *
 * The listener is only installed when the active merchant has
 * barcode_scanning_enabled and the user can manage bookings (operator+).
 * For every other merchant nothing here runs.
 *
 * Routing a scan:
 *   - Screens that want scans (pickup checklist, return prompt, allocation
 *     modal, quick booking) register a handler with useScanHandler(). The most
 *     recently registered active handler receives the scan.
 *   - When none is registered, the fallback handler (GlobalScanHandler, in the
 *     dashboard layout) resolves the code and opens the matching booking.
 *   - An <input data-barcode-input> (the barcode field in the item and product
 *     forms) keeps the scan as its value and no handler is called.
 *
 * When a scan lands in some other text field, the scanned characters are
 * removed from it again, and the Enter is swallowed so it can't submit a form
 * or press a focused button.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useMerchant } from './MerchantContext';
import { normalizeBarcode, playScanFeedback } from '../lib/barcode';

export type ScanHandler = (code: string) => void;

interface ScanContextValue {
  /** Scanning is switched on for this merchant and allowed for this user. */
  enabled: boolean;
  register: (handler: ScanHandler, options?: { fallback?: boolean }) => () => void;
  feedback: (kind: 'ok' | 'error') => void;
}

const ScanContext = createContext<ScanContextValue>({
  enabled: false,
  register: () => () => {},
  feedback: () => {},
});

const MIN_LENGTH = 4;
const MAX_GAP_MS = 35;
/** Some scanners pause slightly before the terminating Enter. */
const MAX_ENTER_GAP_MS = 80;

function isTextInput(el: Element | null): el is HTMLInputElement | HTMLTextAreaElement {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return true;
  if (!(el instanceof HTMLInputElement)) return false;
  return ['text', 'search', 'email', 'tel', 'url', 'number', 'password', ''].includes(el.type);
}

type ValueElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

/** Any other focusable field whose value keystrokes can change (select, date, ...). */
function isOtherValueField(el: Element | null): el is ValueElement {
  return (el instanceof HTMLSelectElement || el instanceof HTMLInputElement) && !isTextInput(el);
}

/** Set a React-controlled field's value so React sees the change. */
function setNativeValue(el: ValueElement, value: string) {
  const proto = Object.getPrototypeOf(el);
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

export function ScanProvider({ children }: { children: React.ReactNode }) {
  const { hasRole } = useAuth();
  const { merchant } = useMerchant();
  const enabled = (merchant as any)?.barcode_scanning_enabled === true && hasRole('operator');

  const handlers = useRef<ScanHandler[]>([]);
  const fallback = useRef<ScanHandler | null>(null);
  // `target` / `saved`: the focused field and its value before the burst began,
  // so a select or date field the scanner typed into can be put back.
  const buffer = useRef<{ chars: string; last: number; target: Element | null; saved: string | null }>({
    chars: '', last: 0, target: null, saved: null,
  });

  const register = useCallback((handler: ScanHandler, options?: { fallback?: boolean }) => {
    if (options?.fallback) {
      fallback.current = handler;
      return () => {
        if (fallback.current === handler) fallback.current = null;
      };
    }
    handlers.current = [...handlers.current, handler];
    return () => {
      handlers.current = handlers.current.filter((h) => h !== handler);
    };
  }, []);

  const feedback = useCallback((kind: 'ok' | 'error') => playScanFeedback(kind), []);

  useEffect(() => {
    if (!enabled) return;

    const reset = () => {
      buffer.current = { chars: '', last: 0, target: null, saved: null };
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) {
        reset();
        return;
      }
      const now = performance.now();
      const b = buffer.current;

      if (e.key === 'Enter') {
        const { chars, target: burstTarget, saved } = b;
        const fast = now - b.last <= MAX_ENTER_GAP_MS;
        reset();
        if (chars.length < MIN_LENGTH || !fast) return;

        e.preventDefault();
        e.stopPropagation();

        const code = normalizeBarcode(chars);
        if (!code) return;

        const target = document.activeElement;
        if (isTextInput(target)) {
          if (target.dataset.barcodeInput !== undefined) {
            // Barcode field: the scan becomes its value (replacing what was there).
            setNativeValue(target, code);
            playScanFeedback('ok');
            return;
          }
          // Any other text field: take the scanned characters back out.
          if (target.value.endsWith(chars)) {
            setNativeValue(target, target.value.slice(0, target.value.length - chars.length));
          }
        } else if (isOtherValueField(target) && target === burstTarget && saved !== null && target.value !== saved) {
          // A select / date field the first scanned character changed: put it back.
          setNativeValue(target, saved);
        }

        const handler = handlers.current[handlers.current.length - 1] ?? fallback.current;
        handler?.(code);
        return;
      }

      if (e.key === 'Shift') return; // scanners press Shift for upper-case characters
      if (e.key.length !== 1) {
        reset();
        return;
      }

      if (b.chars && now - b.last <= MAX_GAP_MS) {
        b.chars += e.key;
        // Nobody types this fast: stop the rest of the burst from changing a
        // select or date field. (Text fields keep the characters; they are
        // removed again once the scan is recognised.)
        if (isOtherValueField(b.target)) e.preventDefault();
      } else {
        const active = document.activeElement;
        b.chars = e.key;
        b.target = active;
        b.saved = isOtherValueField(active) || isTextInput(active) ? active.value : null;
      }
      b.last = now;
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enabled]);

  const value = useMemo(() => ({ enabled, register, feedback }), [enabled, register, feedback]);

  return <ScanContext.Provider value={value}>{children}</ScanContext.Provider>;
}

/** Whether barcode scanning is on for the active merchant and this user. */
export function useBarcodeScanning() {
  const { enabled, feedback } = useContext(ScanContext);
  return { enabled, feedback };
}

/**
 * Receive scans while `active` is true. The most recently registered active
 * handler wins, so a modal opened on top of a page takes over its scans.
 * The latest `handler` is always used; it does not need to be memoised.
 */
export function useScanHandler(handler: ScanHandler, active = true, options?: { fallback?: boolean }) {
  const { enabled, register } = useContext(ScanContext);
  const latest = useRef(handler);
  latest.current = handler;
  const isFallback = options?.fallback ?? false;

  useEffect(() => {
    if (!enabled || !active) return;
    return register((code) => latest.current(code), { fallback: isFallback });
  }, [enabled, active, register, isFallback]);
}
